/**
 * Tenant jurisdiction — the country-aware foundation every other
 * country-dependent surface reads from.
 *
 *   GET /api/v1/tenants/me/jurisdiction  → country, payroll currency,
 *       payroll readiness, calendar policy, feature flags.
 *   GET /api/v1/tenants/me/document-types → the identity documents this
 *       jurisdiction issues, with bilingual labels + validation hints.
 *
 * TanStack's cache is the whole caching story here — no manual ETag/304
 * handling, because the api-client error middleware treats a bodyless 304 as a
 * failure. See the staleness note on the constants below: the jurisdiction is
 * NOT stable within a session, and the document catalog is.
 *
 * Rendering rule: read the `features` flags directly. Never cross-derive one
 * flag from another and never infer a flag from `payrollReadiness`.
 */
import type { UseQueryResult } from "@tanstack/react-query";
import { useTenantQuery } from "./factory";
import { useApiClient } from "../react/ApiProvider";
import { useTenantSlug } from "../react/useTenantSlug";
import { qk } from "../query-keys/qk";
import type { ApiClient } from "../client";
import type { ApiError } from "../errors/ApiError";
import type { components } from "../generated/schema";

export type TenantJurisdiction = components["schemas"]["TenantJurisdictionView"];
export type TenantJurisdictionFeatures = components["schemas"]["TenantJurisdictionFeatures"];
export type TenantDocumentType = components["schemas"]["TenantDocumentTypeView"];
/** Which picker may offer a document type. */
export type DocumentSurface = TenantDocumentType["surfaces"][number];
export type BankCatalog = components["schemas"]["BankCatalogView"];
export type BankView = components["schemas"]["BankView"];
export type CityCatalogView = components["schemas"]["CityCatalogView"];
export type CityView = components["schemas"]["CityView"];

/**
 * A minute — because ACTIVATION is the thing that changes.
 *
 * This used to be an hour, on the premise that "the jurisdiction of a tenant
 * does not change while they work". That premise is now the reason the value
 * was wrong: linkco switching payroll on for a tenant flips `features.payroll`
 * mid-session, and until this refetches the app keeps rendering the "coming
 * soon" fence over a feature the tenant already has. An hour of that is an hour
 * of an admin being told to wait for something they were just given.
 *
 * Nothing can push the change in. Activation happens in Link Admin — a
 * different app, a different session, a different QueryClient — so it cannot
 * invalidate this key, and the backend has no channel to. Going stale on our
 * own and refetching is the ONLY thing that closes the gap, which is why
 * `refetchOnWindowFocus` is turned back on for these two queries against the
 * app-wide default: coming back to the tab is when an admin who was just told
 * "it's on now" looks again.
 *
 * A minute of staleness costs one conditional-free GET per tab per minute of
 * active use, on a response every fenced surface already shares.
 */
const JURISDICTION_STALE_TIME = 60_000;

/**
 * The jurisdiction's `gcTime`, and the document catalog's whole cache policy.
 *
 * Held separately from the stale time on purpose: staleness decides whether to
 * REFETCH, `gcTime` decides whether there is anything to render meanwhile.
 * Collapsing the two would make every fenced screen flash a loading state on
 * the way back from a five-minute detour.
 */
const ONE_HOUR = 60 * 60_000;

type AnyGet = {
  GET: (path: string) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

async function get<T>(client: ApiClient, path: string): Promise<T> {
  const res = await (client as unknown as AnyGet).GET(path);
  if (res.error) throw res.error as ApiError;
  if (res.data === undefined) throw new Error(`Empty response from ${path}`);
  return res.data as T;
}

/** `GET /api/v1/tenants/me/jurisdiction`. */
export function useJurisdictionQuery(
  tenantSlug: string,
): UseQueryResult<TenantJurisdiction, ApiError> {
  const client = useApiClient();
  return useTenantQuery<TenantJurisdiction>(client, {
    queryKey: qk.jurisdiction(tenantSlug),
    staleTime: JURISDICTION_STALE_TIME,
    gcTime: ONE_HOUR,
    // Against the app-wide `refetchOnWindowFocus: false`. See the constant.
    refetchOnWindowFocus: true,
    queryFn: (c) => get<TenantJurisdiction>(c, "/api/v1/tenants/me/jurisdiction"),
  });
}

/**
 * `GET /api/v1/tenants/me/document-types` — the identity-document catalog for
 * the tenant's jurisdiction. Call from identity-document forms.
 *
 * Stays on the hour the jurisdiction gave up: this catalog is a function of the
 * tenant's COUNTRY, which is immutable, and no activation adds a passport type
 * to Saudi Arabia. It is the query the old comment was actually right about.
 */
export function useDocumentTypeCatalog(): UseQueryResult<TenantDocumentType[], ApiError> {
  const client = useApiClient();
  const tenantSlug = useTenantSlug();
  return useTenantQuery<TenantDocumentType[]>(client, {
    queryKey: qk.documentTypeCatalog(tenantSlug),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    queryFn: async (c) => {
      const body = await get<{ documentTypes?: TenantDocumentType[] }>(
        c,
        "/api/v1/tenants/me/document-types",
      );
      return body.documentTypes ?? [];
    },
  });
}

/**
 * `GET /api/v1/tenants/me/banks` — the bank directory behind every bank picker
 * and the name behind an employee's account number.
 *
 * On the hour, for the same reason the document catalog is: this is a function
 * of the tenant's COUNTRY, and a bank merger is not a within-session event. The
 * response is ETag-backed and versioned, but the conditional request is left to
 * the browser cache — the api-client error middleware treats a bodyless 304 as
 * a failure, so this hook never sends `If-None-Match` itself.
 *
 * RETIRED rows are returned and kept: an account opened before a merger still
 * carries the absorbed bank's code, and dropping those rows would show an
 * existing employee as banking nowhere. Filtering to ACTIVE is the PICKER's
 * job, not the cache's.
 */
export function useBankCatalog(): UseQueryResult<BankView[], ApiError> {
  const client = useApiClient();
  const tenantSlug = useTenantSlug();
  return useTenantQuery<BankView[]>(client, {
    queryKey: qk.bankCatalog(tenantSlug),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    queryFn: async (c) => {
      const body = await get<BankCatalog>(c, "/api/v1/tenants/me/banks");
      return body.items;
    },
  });
}

/**
 * `GET /api/v1/tenants/me/cities` (`getTenantMyCities`) — the cities of the
 * company's own country: a branch's city and the answers to a city question.
 * On the hour, like the bank directory: a function of the country.
 */
export function useCityCatalog(): UseQueryResult<CityView[], ApiError> {
  const client = useApiClient();
  const tenantSlug = useTenantSlug();
  return useTenantQuery<CityView[]>(client, {
    queryKey: qk.cityCatalog(tenantSlug),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    queryFn: async (c) => {
      const body = await get<CityCatalogView>(c, "/api/v1/tenants/me/cities");
      return body.items;
    },
  });
}

/* ------------------------------------------------------------------ *
 * Feature predicates — the ONLY place a surface decides it is available.
 * ------------------------------------------------------------------ */

/** Income-tax surfaces: tax fields, bracket traces, the tax authority forms. */
export function incomeTaxEnabled(j: TenantJurisdiction): boolean {
  return j.features.incomeTax;
}

/** End-of-service surfaces: the EOS tab, its routes and the calculator. */
export function eosEnabled(j: TenantJurisdiction): boolean {
  return j.features.eos;
}

/**
 * Core payroll: runs, payslips, bank exports, payroll reports.
 *
 * `payrollReadiness` is informational only — the flag is the answer, and
 * deriving one from the other is exactly what the contract forbids.
 */
export function payrollEnabled(j: TenantJurisdiction): boolean {
  return j.features.payroll;
}

/**
 * The countries whose payroll engine ships in the product today — the mirror of
 * the backend's `JurisdictionCode` enum. Used ONLY to pick which explanation a
 * fenced tenant reads, never to decide whether payroll is on (that stays
 * `features.payroll`). Kept deliberately one-directional: a country missing
 * from this list downgrades the copy to the vague-but-true variant, so a stale
 * list can never make the app claim something false.
 */
const PAYROLL_COUNTRIES: readonly string[] = ["JO", "SA"];

/**
 * Which `coming-soon` bundle key explains a payroll fence to this tenant.
 *
 * Two genuinely different situations were being told the same (wrong) story:
 *
 *   - the product has no payroll engine for this country at all;
 *   - the product HAS one, and this particular organisation has not been
 *     switched on for it yet (a per-tenant certification linkco runs).
 *
 * A Saudi tenant reading "payroll is not switched on for your country" is
 * reading a falsehood — Saudi payroll is generally available. `country` says
 * whether an engine exists; `payrollReadiness` says whether this tenant has
 * been through activation. Anything else (jurisdiction unknown, or the
 * contradictory READY-but-flag-off case) falls back to the neutral key, whose
 * copy asserts nothing about the country and is therefore true in every case.
 */
export function payrollComingSoonKey(j: TenantJurisdiction | null): string {
  if (j === null) return "payroll_jurisdiction";
  if (!PAYROLL_COUNTRIES.includes(j.country)) return "payroll_jurisdiction";
  return j.payrollReadiness === "HR_ONLY" ? "payroll_pending_activation" : "payroll_jurisdiction";
}

/**
 * The GOSI admin surfaces — establishments, per-employee enrollments, the
 * payroll-readiness view. Gated on the SCHEME, never on the country: a second
 * GOSI jurisdiction would light these up without a code change, and a Jordanian
 * tenant on SSC keeps its own contribution surfaces instead.
 */
export function gosiEnabled(j: TenantJurisdiction): boolean {
  return j.features.socialInsurance === "GOSI";
}

/**
 * The SSC contribution surfaces — Jordan's Social Security Corporation rates
 * and its insurable ceiling. The mirror of {@link gosiEnabled}, and gated the
 * same way: on the SCHEME, never on the country. A GOSI tenant reading a
 * screen of Jordanian SSC rates is reading someone else's law.
 */
export function sscEnabled(j: TenantJurisdiction): boolean {
  return j.features.socialInsurance === "SSC";
}
